/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';

import {
    SearchActions,
    Button,
    MediaGalleryModal,
    Icon,
    ContentBlock,
    Inline,
    useBreakpoint,
    pluralize,
    STATUS,
} from 'sarsaparilla';

import { FavoriteHeart } from 'shared-ui/favorites';

import { HeroImageGalleryLayoutMd } from './HeroImageGalleryLayoutMd';
import { HeroImageGalleryLayoutSm } from './HeroImageGalleryLayoutSm';
import { HeroImageGalleryLayoutXl } from './HeroImageGalleryLayoutXl';
import type { HeroImageGalleryBaseProps, Photo } from './HeroImageGallery.types';

import styles from './HeroImageGallery.module.scss';

const { IDLE, PENDING, ERROR, SUCCESS } = STATUS;

export interface HeroImageGalleryProps extends HeroImageGalleryBaseProps {
    galleryImages: Photo[];
    heroImage?: Photo;

    /** API fetch status */
    status: keyof typeof STATUS;
}

export function HeroImageGallery({
    galleryImages = [],
    heroImage,
    status,
    hasThumbnailStrip = true,
    className,
    nearText,
    assetName,
    parentText,
    assetId,
    entityType,
    useMobileLayout,
    imageLoadingStrategy,
}: HeroImageGalleryProps) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { isXlAndUp, isSmAndDown } = useBreakpoint();
    const [images, setImages] = React.useState<Photo[]>([]);
    const didTrackEventRun = React.useRef(false);

    React.useEffect(() => {
        if (status === SUCCESS && !didTrackEventRun.current) {
            const hasHeroImage = !!heroImage;
            const hasGalleryImages = galleryImages.length > 0;
            const gaTrackingObj = {
                category: 'Hero Image Gallery Audit',
                action: '',
                label: window.location.href,
            };

            if (hasHeroImage) {
                gaTrackingObj.action = 'Has Hero Image';
            }

            if (hasHeroImage && hasGalleryImages) {
                gaTrackingObj.action = `Has Hero Image & ${
                    galleryImages.length
                } ${pluralize(galleryImages.length, 'Gallery Image', 'Gallery Images')}`;
            }

            if (!hasHeroImage && hasGalleryImages) {
                gaTrackingObj.action = `No Hero Image - Has ${
                    galleryImages.length
                } ${pluralize(galleryImages.length, 'Gallery Image', 'Gallery Images')}`;
            }

            if (!hasHeroImage && !hasGalleryImages) {
                gaTrackingObj.action = 'No Hero Image or Gallery Images';
            }

            SearchActions.trackRunSearch(gaTrackingObj, () => {
                didTrackEventRun.current = true;
            });
        }
    }, [status, heroImage, galleryImages]);

    React.useEffect(() => {
        const copy: Photo[] = Array.isArray(galleryImages) ? [...galleryImages] : [];
        if (heroImage?.url) {
            copy.unshift(heroImage);
            setImages(copy);
        } else {
            // With no hero image, only promote gallery photos to the hero when
            // there are enough of them for the 3-up mosaic. Fewer than that
            // falls through to the unavailable placeholder below.
            //
            // Always commit a value either way: `useAssetImages` holds the
            // previous asset's photos through a refetch, so skipping the update
            // would leave the last asset's images on screen.
            setImages(copy.length > 2 ? copy : []);
        }
    }, [heroImage, galleryImages]);

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const galleryModal = (
        <MediaGalleryModal
            images={images}
            isOpen={isModalOpen}
            onRequestClose={closeModal}
            activeIndex={0}
            hasThumbnailStrip={hasThumbnailStrip}
            imageLoadingStrategy={imageLoadingStrategy}
        />
    );

    const favoriteHeart = assetId ? (
        <FavoriteHeart entityId={assetId} entityType={entityType ?? 'asset'} />
    ) : null;

    const mobileLayout = useMobileLayout ? (
        <Inline space="xs" className={styles.actionButtons}>
            {images.length > 0 && (
                <Button
                    className={styles.viewMorePhotos}
                    appearance="pill"
                    iconAfterElement={<Icon iconName="photo-gallery" />}
                    onClick={openModal}
                    size="xs"
                >
                    {images.length > 10 ? '10+' : images.length}
                </Button>
            )}
            {favoriteHeart}
        </Inline>
    ) : null;

    // The pill opens `galleryModal`, so branches that don't mount the modal
    // render the favorite heart alone. `images` can still be non-empty on
    // error — `useAssetImages` holds the previous asset's photos through a
    // failed refetch — which would otherwise leave a pill that does nothing.
    const mobileLayoutWithoutGallery = useMobileLayout ? (
        <Inline space="xs" className={styles.actionButtons}>
            {favoriteHeart}
        </Inline>
    ) : null;

    // RENDER LOGIC
    //
    // Case: API request is not finished - show grey box
    if (status === PENDING || status === IDLE) {
        return (
            <div className={cx(styles.heroImageGallery, className)}>
                <HeroImageGalleryLayoutSm />
            </div>
        );
    }

    // Case: API error, or success but no images - show unavailable image
    if (status === ERROR || !images || images.length === 0) {
        return (
            <div className={cx(styles.heroImageGallery, className)}>
                <HeroImageGalleryLayoutSm
                    showUnavailableImage
                    nearText={nearText}
                    assetName={assetName}
                    parentText={parentText}
                />
                {mobileLayoutWithoutGallery}
            </div>
        );
    }

    // Case: Less than 3 images - show single image
    if (images.length < 3) {
        return (
            <div className={cx(styles.heroImageGallery, className)}>
                {mobileLayout}
                <HeroImageGalleryLayoutSm
                    image={images[0]}
                    nearText={nearText}
                    assetName={assetName}
                    parentText={parentText}
                />
                {galleryModal}
            </div>
        );
    }

    // Case: HAPPY PATH 3+ IMAGES!
    return (
        <div>
            <div className={cx(styles.heroImageGallery, className)}>
                {isXlAndUp ? (
                    <HeroImageGalleryLayoutXl images={images} />
                ) : isSmAndDown ? (
                    <HeroImageGalleryLayoutSm
                        image={images[0]}
                        nearText={nearText}
                        assetName={assetName}
                        parentText={parentText}
                    />
                ) : (
                    <HeroImageGalleryLayoutMd images={images} />
                )}

                {useMobileLayout ? (
                    mobileLayout
                ) : (
                    <ContentBlock className={styles.viewMoreButtonWrap}>
                        <Button
                            size="xs"
                            appearance="solid-white"
                            iconBeforeElement={<Icon iconName="photo-gallery" />}
                            onClick={openModal}
                            className={styles.viewMoreButton}
                        >
                            View Photos
                        </Button>
                    </ContentBlock>
                )}
            </div>

            {galleryModal}
        </div>
    );
}
